import { removeRightWhile } from "@aprao/common/src/array/removeRightWhile.js";
import {
  bigFractionFromFraction,
  bigFractionFromNumber,
} from "@aprao/common/src/math/BigFraction.js";
import { RtPositiveInteger } from "@aprao/common/src/runtypes/common.js";
import type {
  Fraction,
  WithProjectionPeriods,
} from "@aprao/common/src/shapes/common.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable } from "mobx";
import { createErr, createOk } from "option-t/plain_result";
import { z } from "zod";

import { MAX_PERIOD_COUNT, RtPeriodIndex } from "./utils.js";
import type { WithProjectionPeriodsDelegate } from "./WithProjectionPeriodsDelegate.js";

const RtSpreadParams = z
  .object({
    periodIndex: RtPeriodIndex,
    duration: RtPositiveInteger,
    growthPercentage: z.number().min(0),
  })
  .refine((value) => value.periodIndex + value.duration <= MAX_PERIOD_COUNT)
  .brand<"spreadParams">();

type SpreadParams = z.infer<typeof RtSpreadParams>;

/**
 * A delegate where the total value is uncapped contrary to the regular
 * {@link WithProjectionPeriodsDelegate}.
 */
export class WithUncappedProjectionPeriodsDelegate {
  constructor(readonly $model: WithProjectionPeriods) {
    makeAutoObservable(this);
  }

  get duration() {
    return this.$model.projectionPeriods.length;
  }

  getPortionAtPeriodIndex(periodIndex: number) {
    if (this.$model.projectionPeriods.length <= periodIndex) {
      return null;
    }

    const portion = this.$model.projectionPeriods[periodIndex];
    return portion == null ? null : bigFractionFromFraction(portion);
  }

  _setPortionAtPeriodIndex(periodIndex: number, portion: Fraction | null) {
    if (this.$model.projectionPeriods.length <= periodIndex) {
      this.$model.projectionPeriods.push(
        ...times(
          periodIndex - this.$model.projectionPeriods.length + 1,
          () => null,
        ),
      );
    }

    this.$model.projectionPeriods[periodIndex] = portion;

    // Maintain the portions array in good shape by trimming `null` tail when
    // necessary. Setting a period to `null` may have created a tail of
    // continuous `null`.
    removeRightWhile(
      this.$model.projectionPeriods,
      (_portion) => _portion != null,
    );
  }

  validateSpread(params: {
    periodIndex: number;
    duration: number;
    growthPercentage: number;
  }) {
    const parsedParams = RtSpreadParams.safeParse(params);
    return parsedParams.success ?
        createOk<SpreadParams>(parsedParams.data)
      : createErr("Invalid spread parameters.");
  }

  spread(params: SpreadParams) {
    this.$model.projectionPeriods.length = 0;

    times(params.duration, (periodIndex) => {
      const rate = 1 + params.growthPercentage / 100;

      this._setPortionAtPeriodIndex(
        params.periodIndex + periodIndex,
        bigFractionFromNumber(rate ** periodIndex).toFraction(),
      );
    });
  }
}
